import { Component, inject, LOCALE_ID, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core'
import { AbstractControl, NgForm, FormsModule } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { Subscription } from 'rxjs'

import { ConfirmationService, MessageService, SelectItem, SelectItemGroup, PrimeTemplate } from 'primeng/api'
import { ButtonDirective } from 'primeng/button'
import { Checkbox } from 'primeng/checkbox'
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog'
import { FocusTrap } from 'primeng/focustrap'
import { Ripple } from 'primeng/ripple'
import { Select } from 'primeng/select'

import { Tag } from 'primeng/tag'
import { Tooltip } from 'primeng/tooltip'
import { forEach, isEmpty, isNil, isNumber, map } from 'lodash-es'

import { ArchiveDocumentTransition, CUSTOM_CONTROLLER_DOC_TYPES } from '@app/constants/constants'
import { ArchiveDocumentField, ArchiveDocumentForm, ArchiveDocumentType, newArchiveDocument } from '@app/interfaces/archive-documents'
import { newPlace, Place } from '@app/interfaces/place'
import { toArchiveDocumentForm } from '@app/apis/archive-documents.adapter'
import { ArchiveDocumentsApi } from '@app/apis/archive-documents.api'
import { toArchiveDocumentUpdatePayload } from '@app/apis/archive-documents.payload'
import { DocumentService } from '@app/services/document.service'
import { Signal, SignalService } from '@app/services/signal.service'
import { AuditLogListComponent } from '@app/components/audit-log/audit-log-list'
import { DetailMode } from '@app/components/generic/generic.constants'
import type { ArchiveDocumentType as GeneratedArchiveDocumentType, CountryListItem, PaginatedArchiveDocumentTypeList } from '@app/generated/api/model'
import { TypeGenericComponent } from './types/type-generic/type-generic.component'

@Component({
  selector: 'app-metadata',
  templateUrl: 'metadata.component.html',
  styleUrl: 'metadata.component.less',
  encapsulation: ViewEncapsulation.None,
  imports: [
    FormsModule,
    FocusTrap,
    ButtonDirective,
    Ripple,
    Tooltip,
    Checkbox,
    Select,
    PrimeTemplate,
    Tag,
    TypeGenericComponent,
  ],
})
export class MetadataComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router)
  private readonly activatedRoute = inject(ActivatedRoute)
  private readonly messageService = inject(MessageService)
  public readonly archiveDocumentsApi = inject(ArchiveDocumentsApi)
  private readonly signalService = inject(SignalService)
  private readonly documentService = inject(DocumentService)
  public readonly dialogService = inject(DialogService)
  public readonly config = inject(DynamicDialogConfig)
  public readonly ref = inject(DynamicDialogRef)
  public readonly confirmationService = inject(ConfirmationService)
  public readonly locale = inject(LOCALE_ID)

  // active document will be updated automatically from the currently activated route
  public document?: ArchiveDocumentForm
  public backup?: ArchiveDocumentForm
  public routeSubscription!: Subscription
  public signalServiceSubscription?: Subscription

  // The default behavior is to respect the 'required' attribute of the fields for each type.
  // This may be overridden in 'bulk update' mode.
  public respectRequired = true
  public documentUuids: string[] = []

  // whether to include all the 'individual' document types in the list
  public showAllDocTypes: boolean = false

  // this is for the (grouped) types menu
  public documentTypeGroups: SelectItemGroup[] = []

  // the list of all possible document fields, i.e. for all types
  public allDocumentTypes: GeneratedArchiveDocumentType[] = []
  // the list of fields for the active document
  public documentFields: ArchiveDocumentField[] = []

  // load document types configs and the list of types that will use a custom form controller, the rest will fall back to generic
  public customControllers: string[] = CUSTOM_CONTROLLER_DOC_TYPES

  public filterLabelField: 'label_en' | 'label_fr' = 'label_en'
  public mode = 'normal'
  public bulkMode = false
  public toolTips: { [key: string]: string } = {}
  protected readonly DetailMode = DetailMode

  @ViewChild('f', { static: true }) public detailForm!: NgForm
  @ViewChild('select') select?: Select

  ngOnInit (): void {
    if (this.locale === 'fr') {
      this.filterLabelField = 'label_fr'
    }

    this.loadAllDocumentTypes().add(() => {
      if (this.config.data?.documents) {
        this.bulkMode = true
        this.documentUuids = map(this.config.data?.documents, (document: ArchiveDocumentForm) => {
          return document.uuid
        })
        this.document = newArchiveDocument()
        this.document.type = undefined
        this.respectRequired = false
        this.mode = 'edition'
      } else {
        // once the list of doc types has loaded we are ready to load any docs as necessary, according to the active route
        this.routeSubscription = this.activatedRoute.params.subscribe(routeParams => {
          // route changed, load new doc
          this.fetchDoc(routeParams.code)
        })
      }
    })

    this.signalServiceSubscription = this.signalService.getSignal().subscribe(signal => {
      if (signal === Signal.SIGNAL_REFRESH_METADATA) {
        if (!this.bulkMode) {
          if (this.document) this.fetchDoc(this.document.uuid)
        }
      }
    })

    this.toolTips.showAllTypes = $localize`:@@metadata-show-all-types-tooltip:Show all available document types.\n\nIf this is not checked only 'batch' types are shown.`
  }

  ngOnDestroy (): void {
    this.routeSubscription?.unsubscribe()
  }

  onShowAllDocTypesChange () {
    this.loadAllDocumentTypes()
  }

  loadAllDocumentTypes () : Subscription {
    const params = { limit: 1000, ordering: 'sort_order,' + this.filterLabelField, archives_user_filter: true }
    return this.archiveDocumentsApi.listDocTypes(params).subscribe((res: PaginatedArchiveDocumentTypeList) => {
      // populate global `allDocumentTypes` list, containing all the possible types and the fields they can contain
      this.allDocumentTypes = res.results
      this.buildDocumentTypeGroups(this.document?.type?.code)
    })
  }

  buildDocumentTypeGroups (forceTypeCode?: string) {
    // now build the grouped structure suitable for the prime-ng autocomplete widget
    // https://www.primefaces.org/primeng/autocomplete
    this.documentTypeGroups = []

    this.allDocumentTypes.forEach(documentType => {
      // if the incoming type is 'type unknown', don't display it
      if (documentType.code === 'TYPE_UNKNOWN') return

      // if we are not listing 'all types', only list 'priority' documents, unless we were
      // specifically told to include this document via the `forceTypeId` param
      const thisTypeIsForced = (forceTypeCode === documentType.code)
      if (!documentType.priority && !this.showAllDocTypes && !thisTypeIsForced) return

      // determine if a group exists already in our structure, if so get a handle on it
      let group = this.documentTypeGroups.find((existingGroups: SelectItemGroup) => {
        return documentType.department?.code === existingGroups.value
      })
      // if it didn't exist, create it
      if (group === undefined) {
        const label = documentType.department ? documentType.department[this.filterLabelField] : $localize`:@@search-list-type-unknown:Unknown`
        const count = this.documentTypeGroups.push({ value: documentType.department?.code, label: label, items: [] })
        group = this.documentTypeGroups[count - 1]
      }

      // now the group is guaranteed to exist, append our item (documentType) to it in the syntax required by prime-ng
      let label = documentType[this.filterLabelField]
      if (!documentType.batch) {
        label = `${documentType.code} : ${label}`
      }
      const newItem: SelectItem = {
        label: label,
        value: { code: documentType.code, batch: documentType.batch, priority: documentType.priority },
      }
      group.items.push(newItem)
    })
  }

  /**
   * Display (or not) a warning window regarding empty recommended fields, then submit the form data to the server
   */
  onSave (): void {
    if (!this.document) return
    const document = this.document

    const toBeConfirmedFields = this.documentFields.filter((field: ArchiveDocumentField) => {
      // determine if the value is a string or array, and detect if it has a value accordingly
      const theFieldValue = document[field.name]
      if (Array.isArray(theFieldValue)) {
        return field.recommended && theFieldValue.length === 0
      } else {
        return field.recommended && isNil(theFieldValue)
      }
    })

    if (toBeConfirmedFields.length > 0) {
      // display the warning window
      const messageToBeConfirmedFields = toBeConfirmedFields.map((field: ArchiveDocumentField) => field[this.filterLabelField]).join(', ')
      this.confirmationService.confirm({
        message: $localize`:@@metadata-empty-recommended-fields:You did not fill field(s): ${messageToBeConfirmedFields}, are you sure that you want to save ?`,
        accept: () => {
          this.performSave()
        },
      })
    } else {
      // otherwise save without asking
      this.performSave()
    }
  }

  /**
   * Submit the form individually or in bulk mode
   */
  performSave (): void {
    if (!this.document) return

    // clear the hints following the initial save
    this.document.hints = {}

    // if we're in bulk mode then use the bulk endpoint
    if (this.bulkMode) {
      const bulkPayload = toArchiveDocumentUpdatePayload(this.document, this.documentFields, 'bulk')
      // eslint-disable-next-line @typescript-eslint/naming-convention
      this.archiveDocumentsApi.updateMultiple(this.documentUuids, bulkPayload).subscribe((res: { documents_updated: number }) => {
        // if we don't do this in a setTimeout() there is a strange redirect which refreshes the page, don't know why :|
        const numDocuments = res.documents_updated
        this.messageService.add({
          severity: 'success',
          life: 3000,
          summary: '',
          detail: $localize`:@@metadata-x-documents-updated:${numDocuments} documents updated`,
        })
        this.ref.close(this.document)
      })
      return
    }

    // otherwise perform an individual save
    const payload = toArchiveDocumentUpdatePayload(this.document, this.documentFields, 'single')
    this.archiveDocumentsApi.update(this.document, payload).subscribe(res => {
      this.document = toArchiveDocumentForm(res)
      // notify subscribers of new document
      this.documentService.setDocument(this.document)
      this.messageService.add({ severity: 'success', life: 1000, summary: '', detail: 'Document saved' })
      this.mode = 'normal'
    })
  }

  /**
   * User clicks on 'Edit', enter edit mode and apply the document hints if they exist.
   */
  onEdit (): void {
    // make backup in case the user cancels the modif
    this.backup = structuredClone(this.document)
    this.mode = 'edition'

    if (!this.document?.type) {
      this.applyHints()
    }

    // auto focus the doc-type dropdown
    setTimeout(() => {
      this.select?.applyFocus()
    }, 50)
  }

  onDelete (): void {
    this.confirmationService.confirm({
      message: $localize`:@@metadata-are-you-sure-delete-doc:Are you sure that you want to delete this document ?`,
      accept: () => {
        if (!this.document) return
        this.archiveDocumentsApi.delete(this.document).subscribe(() => {
          this.signalService.sendSignal(Signal.SIGNAL_REFRESH_DRAFTS)
          this.router.navigate(['/search', 'list'])
        })
      },
    })
  }

  onCancel () {
    // if we're in bulk mode close the modal
    if (this.bulkMode) {
      // if we don't do this in a setTimeout() there is a strange redirect which refreshes the page, don't know why :|
      setTimeout(() => { this.ref.close() })
      return
    }

    // restore the backup state
    this.document = this.backup
    this.mode = 'normal'
    // ensure the fields match the current doc
    this.recomputeDocumentFields()
  }

  /**
   * Return whether the incoming value is empty, or a number, which blocks us from using isEmpty on its own :|
   */
  private _isBlank (value: unknown) {
    return isEmpty(value) && !isNumber(value)
  }

  /**
   * Return whether the current form is 'complete', i.e. all required fields have been entered.
   *
   * We can't use form.isValid for this because that doesn't work when the fields are disabled, i.e. when not in edit mode.
   */
  formIsIncomplete () : boolean {
    let result = false
    forEach(this.detailForm.controls, (control: AbstractControl) => {
      const validator = control.validator?.({} as AbstractControl)
      if (validator?.required && this._isBlank(control.value)) {
        result = true
      }
    })
    return result
  }

  /**
   * Validate the document via the workflow API
   */
  onValidate () {
    if (!this.document) return

    this.archiveDocumentsApi.play(this.document.uuid, ArchiveDocumentTransition.VALIDATE).subscribe(() => {
      // call was success, emit new detail which will cascade back down to this component
      this.fetchDoc(this.document!.uuid)
      this.signalService.sendSignal(Signal.SIGNAL_REFRESH_DRAFTS)
      this.messageService.add({
        severity: 'success',
        life: 3000,
        summary: '',
        detail: $localize`:@@metadata-document-validated:Document validated`,
      })
    })
  }

  /**
   * update the document from any hints that may have been given for the fields that don't yet have values
   */
  safeUpdateHint (fieldName: string) {
    if (!this.document) return

    const hint = this.document.hints[fieldName]
    if (hint !== undefined && hint !== null && !this.document[fieldName]) {
      this.document[fieldName] = hint
    }
  }

  /**
   * Called when then document loads, if we were given any hints by the backend then apply them now.
   *
   * Note that the backend removes hints after the first save so there is no risk of overwriting 'good' data.
   */
  applyHints () {
    if (!this.document) return

    // update the type using the code loaded from the hint / doc types serializer fetch
    this.allDocumentTypes.forEach(documentType => {
      if (documentType.code === this.document?.hints.type) {
        this.document.type = toArchiveDocumentType(documentType)
        this.buildDocumentTypeGroups(documentType.code)
      }
    })

    // reset the list of visible fields according to the selected type
    this.recomputeDocumentFields()

    // Relation controls require complete local models, not arbitrary JSON hint objects.
    const country = toCountryHint(this.document.hints.country)
    if (country) this.document.country = country
    const place = toPlaceHint(this.document.hints.place)
    if (place) this.document.place = place

    // finally update the 'simple' fields
    const hintedFieldNames = [
      'date',
      'project_codes',

      'voucher_code',
      'budget_line',
      'balzac_book',

      'asset_code',
      'asset_categories',
      'warehouse_code',
      'supplier_code',
      'supplier_name',
      'document_number',
      'item_category',
      'order_form_date',

      'staff_name',
      'third_party_code',
      'homere_code',

      'activity',
    ]
    hintedFieldNames.forEach(fieldName => this.safeUpdateHint(fieldName))
  }

  recomputeDocumentFields () {
    // get the list of ArchiveDocumentType for this specific document from our global list
    const docType = this.allDocumentTypes.find(archiveDocumentType => {
      return archiveDocumentType.code === this.document?.type?.code
    })
    this.documentFields = toArchiveDocumentFields(docType?.archive_document_fields)
  }

  /**
   * Load document detail
   */
  fetchDoc (docCode: string): void {
    this.archiveDocumentsApi.retrieve(docCode).subscribe(res => {
      this.mode = 'normal'
      this.document = toArchiveDocumentForm(res)

      if (this.document.type?.code === 'TYPE_UNKNOWN') {
        this.document.type = undefined
      }
      this.recomputeDocumentFields()

      // A sad side effect of having the filtered list of ArchiveDocumentTypes is that we can potentially
      //   load a document with a type that is not loaded in the list.
      // Here we ensure that the newly-loaded document's type is in the list.
      // If it is not, we call the rebuild forcing the inclusion of the desired type, regardless of the
      //   'show all types' checkbox.
      let found = false
      forEach(this.documentTypeGroups, (group: SelectItemGroup) => {
        forEach(group.items, (item: SelectItem) => {
          if (this.document?.type === item.value) {
            found = true
          }
        })
      })
      if (!found) {
        // our desired type is not in the list, call the rebuild forcing the desired type
        this.buildDocumentTypeGroups(this.document?.type?.code)
      }

      this.documentService.setDocument(this.document)
    })
  }

  onClickHistory (code: string) {
    this.dialogService.open(AuditLogListComponent, {
      header: $localize`:@@audit-log-object-history:Object history`,
      width: '80rem',
      data: {
        code: code,
      },
      dismissableMask: true,
      focusOnShow: false,
      modal: true,
    })
  }
}

function toArchiveDocumentType (documentType: GeneratedArchiveDocumentType): ArchiveDocumentType {
  return {
    id: documentType.id,
    department: documentType.department,
    code: documentType.code,
    priority: documentType.priority,
    batch: documentType.batch,
    label_en: documentType.label_en,
    label_fr: documentType.label_fr,
    archive_document_fields: toArchiveDocumentFields(documentType.archive_document_fields),
  }
}

function toArchiveDocumentFields (value: unknown): ArchiveDocumentField[] {
  if (!Array.isArray(value)) return []
  return value.filter(isArchiveDocumentField)
}

function isArchiveDocumentField (value: unknown): value is ArchiveDocumentField {
  if (value === null || typeof value !== 'object') return false
  return ['name', 'label_en', 'label_fr', 'type'].every(fieldName => typeof Object.entries(value).find(([name]) => name === fieldName)?.[1] === 'string')
}

function toCountryHint (value: unknown): Pick<CountryListItem, 'id' | 'code_iso' | 'code_iso_2' | 'label_en' | 'label_fr'> | undefined {
  const id = numberProperty(value, 'id')
  const codeIso = stringProperty(value, 'code_iso')
  const labelEn = stringProperty(value, 'label_en')
  const labelFr = stringProperty(value, 'label_fr')
  if (id === undefined || codeIso === undefined || labelEn === undefined || labelFr === undefined) return undefined

  return { id, code_iso: codeIso, code_iso_2: null, label_en: labelEn, label_fr: labelFr }
}

function toPlaceHint (value: unknown): Place | undefined {
  const id = numberProperty(value, 'id')
  const code = stringProperty(value, 'code')
  const name = stringProperty(value, 'name')
  if (id === undefined || code === undefined || name === undefined) return undefined

  const place = newPlace()
  place.id = id
  place.code = code
  place.name = name
  return place
}

function stringProperty (value: unknown, name: string): string | undefined {
  const property = objectProperty(value, name)
  return typeof property === 'string' ? property : undefined
}

function numberProperty (value: unknown, name: string): number | undefined {
  const property = objectProperty(value, name)
  return typeof property === 'number' ? property : undefined
}

function objectProperty (value: unknown, name: string): unknown {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined
  return Object.entries(value).find(([propertyName]) => propertyName === name)?.[1]
}
